import { computed, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';

import { GuideLineEnum } from '@/helpers/enums/guide-line.enum';
import { adminEnumRoutes } from '@/routes/admin/admin.enum.routes.ts';

import useGuideLineComposable from './useGuideLine.composable';

// The 5-step client-facing tour is modelled as 6 anchor points: "Journey
// Planner" is taught as (a) find it in the nav, then (b) use the
// create-module CTA once you're on the page.
//
// `routeName` is the route the tour navigates to when a step becomes active
// ('' = stay on whatever page the admin is already on; used for the
// welcome/finish cards and the nav highlight, since the sidebar is visible
// on every admin page).
//
// `selector` is a CSS selector for the real DOM element to spotlight. The
// nav item is matched by its resolved href (`$=` survives a base-path
// prefix) so we never have to touch the shared sidebar component in
// packages/common-components, which the student app also depends on. The
// other anchors are `data-tour="..."` attributes added at the exact spot in
// each page component.
export interface AdminTourStep {
  key: string;
  routeName: string;
  selector: string | null;
  titleKey: string;
  bodyKey: string;
  isFinish?: boolean;
}

export const adminTourSteps: AdminTourStep[] = [
  {
    key: 'welcome',
    routeName: '',
    selector: null,
    titleKey: 'onboarding.tour.welcomeTitle',
    bodyKey: 'onboarding.tour.welcomeBody',
  },
  {
    key: 'journey-nav',
    routeName: '',
    selector: `a[href$="${adminEnumRoutes.journeys.path}"]`,
    titleKey: 'onboarding.tour.journeyNavTitle',
    bodyKey: 'onboarding.tour.journeyNavBody',
  },
  {
    key: 'journey-cta',
    routeName: adminEnumRoutes.journeys.name,
    selector: '[data-tour="journey-create-module-cta"]',
    titleKey: 'onboarding.tour.journeyCtaTitle',
    bodyKey: 'onboarding.tour.journeyCtaBody',
  },
  {
    key: 'users-column',
    routeName: adminEnumRoutes.users.name,
    selector: '[data-tour="onboarding-column"]',
    titleKey: 'onboarding.tour.usersColumnTitle',
    bodyKey: 'onboarding.tour.usersColumnBody',
  },
  {
    key: 'onboarding-widget',
    // KP-60: the onboarding widget lives on the consolidated Dashboard now
    // (WatarDashboard) — the tour highlights it where admins actually land,
    // not on the nav-hidden Internal Training page.
    routeName: adminEnumRoutes.dashboard.name,
    selector: '[data-tour="onboarding-widget"]',
    titleKey: 'onboarding.tour.widgetTitle',
    bodyKey: 'onboarding.tour.widgetBody',
  },
  {
    key: 'finish',
    routeName: '',
    selector: null,
    titleKey: 'onboarding.tour.finishTitle',
    bodyKey: 'onboarding.tour.finishBody',
    isFinish: true,
  },
];

// Multi-page persistence: unlike the boolean-only `guide-line` map (one
// show/hide flag per feature), a mid-tour admin can hard-refresh or reopen
// the tab, so we also need to remember *which step* they were on. That's a
// small shape the existing map can't hold, hence a dedicated key here.
// Cleared on logout alongside 'guide-line' (see auth.pinia.ts).
export const TOUR_PROGRESS_STORAGE_KEY = 'admin-onboarding-tour-progress';

interface TourProgress {
  active: boolean;
  stepIndex: number;
}

const state = reactive<TourProgress>({ active: false, stepIndex: 0 });

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(TOUR_PROGRESS_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<TourProgress>;
    state.active = !!parsed.active;
    state.stepIndex =
      Number.isInteger(parsed.stepIndex) &&
      (parsed.stepIndex as number) >= 0 &&
      (parsed.stepIndex as number) < adminTourSteps.length
        ? (parsed.stepIndex as number)
        : 0;
  } catch {
    // Corrupt/foreign value under this key — start clean rather than throw.
  }
};
loadProgress();

watch(
  () => ({ active: state.active, stepIndex: state.stepIndex }),
  (value) => {
    localStorage.setItem(TOUR_PROGRESS_STORAGE_KEY, JSON.stringify(value));
  }
);

const useAdminTourComposable = () => {
  const router = useRouter();
  // useGuideLineComposable() snapshots localStorage into a plain closure
  // variable at *invocation* time; its noShowAgain() writes through that
  // same snapshot, so calling it on a composable instance whose snapshot
  // predates any isGuideLocaleStorage() seed throws ("Cannot set properties
  // of null"). BaseGuideLine.vue avoids this only because it happens to call
  // useGuideLineComposable() fresh in both places (onBeforeMount, then again
  // inside the click handler), by which point localStorage is already
  // seeded. We do the same: seed once up front (also telling us whether the
  // tour was already dismissed), then get a *fresh* instance for
  // noShowAgain() so its snapshot is guaranteed to see that seed.
  const notYetDismissed = useGuideLineComposable().isGuideLocaleStorage(
    GuideLineEnum.AdminOnboardingTour
  );
  const dismissTour = () =>
    useGuideLineComposable().noShowAgain(GuideLineEnum.AdminOnboardingTour);

  const currentStep = computed<AdminTourStep | null>(
    () => adminTourSteps[state.stepIndex] ?? null
  );
  const isActive = computed(() => state.active && !!currentStep.value);
  const stepIndex = computed(() => state.stepIndex);
  const stepNumber = computed(() => state.stepIndex + 1);
  const totalSteps = adminTourSteps.length;

  const isStepActive = (key: string) =>
    isActive.value && currentStep.value?.key === key;

  const navigateForStep = async (step: AdminTourStep) => {
    if (step.routeName && router.currentRoute.value.name !== step.routeName) {
      await router.push({ name: step.routeName });
    }
  };

  const finish = () => {
    state.active = false;
    state.stepIndex = 0;
    dismissTour();
  };

  const start = async () => {
    state.stepIndex = 0;
    state.active = true;
    await navigateForStep(adminTourSteps[0]);
  };

  // Called from BaseAdminLayout once the org config is known. Only ever
  // auto-starts once per admin (guide-line 'seen' flag), on internal-team
  // orgs with the onboarding feature on, and never interrupts a tour
  // already in progress (e.g. resumed after a hard refresh).
  const maybeAutoStart = async (
    internalTeamMode: boolean,
    onboardingJourneyEnabled: boolean
  ) => {
    if (!internalTeamMode || !onboardingJourneyEnabled) return;
    if (state.active) return;
    if (!notYetDismissed) return;
    await start();
  };

  const next = async () => {
    const step = currentStep.value;
    if (!step) return;
    if (step.isFinish) {
      finish();
      return;
    }
    const nextIndex = state.stepIndex + 1;
    if (nextIndex >= adminTourSteps.length) {
      finish();
      return;
    }
    state.stepIndex = nextIndex;
    await navigateForStep(adminTourSteps[nextIndex]);
  };

  const back = async () => {
    if (state.stepIndex === 0) return;
    const prevIndex = state.stepIndex - 1;
    state.stepIndex = prevIndex;
    await navigateForStep(adminTourSteps[prevIndex]);
  };

  const skip = () => {
    finish();
  };

  // "Show tour again" — explicit re-open, bypasses the seen flag.
  const restart = async () => {
    await start();
  };

  return {
    steps: adminTourSteps,
    totalSteps,
    currentStep,
    stepIndex,
    stepNumber,
    isActive,
    isStepActive,
    maybeAutoStart,
    start,
    next,
    back,
    skip,
    finish,
    restart,
  };
};

export default useAdminTourComposable;
